import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DeleteMemberReportGQL, MemberReportsGQL } from 'src/gql/generated';
import { type MemberReport, toMemberReports } from '../utils/member-report';

/** みんなの報告（LINE の仕入れ報告など）を、ヤマダ・エディオンの在庫一覧用に取る */
@Injectable({ providedIn: 'root' })
export class MemberReportService {
  constructor(
    private reportsGql: MemberReportsGQL,
    private deleteGql: DeleteMemberReportGQL,
  ) {}

  /** 直近 days 日（既定 30）の報告のうち、店名に chainKeyword（「ヤマダ」「エディオン」）を含むもの */
  async loadRecent(chainKeyword: string, days?: number): Promise<MemberReport[]> {
    const resp = await firstValueFrom(this.reportsGql.fetch({ variables: { days: days ?? null } }));
    const rows = (resp.data?.memberReports ?? []).map((r) => ({
      id: r.id,
      jan: r.jan,
      price: r.price,
      storeName: r.storeName,
      memberName: r.memberName,
      comment: r.comment,
      reportedAt: r.reportedAt,
      // 画面の下の一覧（在庫一覧と同じ形）で画像・Keepa・Amazon 価格を出すため
      product: r.product
        ? {
            jan: r.product.jan,
            amazonProducts: (r.product.amazonProducts ?? []).map((ap) => ({
              asin: ap.asin,
              isMain: ap.isMain,
              name: ap.name,
              imageUrl: ap.imageUrl,
              salesCommission: ap.salesCommission,
              categoryFee: ap.categoryFee,
              fbaShippingFee: ap.fbaShippingFee,
              latestPrice: ap.latestPrice,
              lastKnownPrice: ap.lastKnownPrice ? { price: ap.lastKnownPrice.price, at: ap.lastKnownPrice.at } : null,
              keepaProduct: ap.keepaProduct
                ? {
                    boughtPastMonth: ap.keepaProduct.boughtPastMonth,
                    latestPrice: ap.keepaProduct.latestPrice,
                    lastKnownPrice: ap.keepaProduct.lastKnownPrice
                      ? { price: ap.keepaProduct.lastKnownPrice.price, at: ap.keepaProduct.lastKnownPrice.at }
                      : null,
                    keepaUpdatedAt: ap.keepaProduct.keepaUpdatedAt,
                  }
                : null,
            })),
          }
        : null,
    }));
    return toMemberReports(rows, chainKeyword);
  }

  /** 報告を 1 件消す（同じ投稿が二重に入った時など）。らくじゃん側でユーザ管理の権限が要る（無ければエラー） */
  async delete(id: number): Promise<void> {
    const resp = await firstValueFrom(this.deleteGql.mutate({ variables: { id } }));
    if (!resp.data?.deleteMemberReport) {
      throw new Error('削除できませんでした');
    }
  }
}
